import { useCallback, useEffect, useState } from "react";
import { ApiError, apiRequest } from "../api";
import type {
  AdminGlobalSettings,
  AdminStats,
  AdminUserSummary,
  AdminUsersPage,
  AdminUserUpdate,
  LatestPlanSnapshot,
  MealLog,
  NutritionEstimate,
  PlanProfile,
  PlanResponse,
  RecoveryMetrics,
  RecoveryMetricsMap,
  TokenResponse,
  User,
  WearableConnection,
  WorkoutSession,
} from "../types";
import { aggregateRecoveryMetrics, uuid } from "../utils";

interface UseAppDataOptions {
  token: string;
  onUnauthorized: () => void;
}

function messageFromError(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong.";
}

export function useAppData({ token, onUnauthorized }: UseAppDataOptions) {
  const [user, setUser] = useState<User | null>(null);
  const [snapshot, setSnapshot] = useState<LatestPlanSnapshot | null>(null);
  const [sessions, setSessions] = useState<WorkoutSession[]>([]);
  const [meals, setMeals] = useState<MealLog[]>([]);
  const [wearables, setWearables] = useState<WearableConnection[]>([]);
  const [metrics, setMetrics] = useState<RecoveryMetricsMap>({});
  const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUsersPage | null>(null);
  const [adminSettings, setAdminSettings] = useState<AdminGlobalSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const request = useCallback(async <T,>(path: string, options: RequestInit = {}) => {
    try {
      return await apiRequest<T>(path, options, token);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onUnauthorized();
      throw caught;
    }
  }, [onUnauthorized, token]);

  const fetchLatestPlan = useCallback(async () => {
    try {
      const value = await request<LatestPlanSnapshot | undefined>("/plan/latest");
      return value || null;
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) return null;
      throw caught;
    }
  }, [request]);

  const fetchMetrics = useCallback(async (connections: WearableConnection[]) => {
    const entries = await Promise.all(
      connections
        .filter((item) => item.connected)
        .map(async (item) => {
          try {
            const value = await request<RecoveryMetrics>(`/wearables/${item.provider}/metrics`);
            return [item.provider, value] as const;
          } catch (caught) {
            return [item.provider, { error: messageFromError(caught) }] as const;
          }
        }),
    );
    return Object.fromEntries(entries) as RecoveryMetricsMap;
  }, [request]);

  const refreshSessions = useCallback(async () => {
    if (!token || user?.is_admin) return;
    const value = await request<WorkoutSession[]>("/workouts/sessions?limit=100");
    setSessions(value);
  }, [request, token, user?.is_admin]);

  const refreshMeals = useCallback(async () => {
    if (!token || user?.is_admin) return;
    const value = await request<MealLog[]>("/nutrition/meals?limit=365");
    setMeals(value);
  }, [request, token, user?.is_admin]);

  const refreshWearables = useCallback(async () => {
    if (!token || user?.is_admin) return;
    const connections = await request<WearableConnection[]>("/wearables");
    const nextMetrics = await fetchMetrics(connections);
    setWearables(connections);
    setMetrics(nextMetrics);
  }, [fetchMetrics, request, token, user?.is_admin]);

  const loadAdminStats = useCallback(async () => {
    if (!token) return null;
    const value = await request<AdminStats>("/admin/stats");
    setAdminStats(value);
    return value;
  }, [request, token]);

  const loadAdminUsers = useCallback(async (search = "", page = 1, pageSize = 25) => {
    if (!token) return null;
    const query = new URLSearchParams({
      search,
      page: String(page),
      page_size: String(pageSize),
    });
    const value = await request<AdminUsersPage>(`/admin/users?${query}`);
    setAdminUsers(value);
    return value;
  }, [request, token]);

  const updateAdminUser = useCallback(async (userId: number, update: AdminUserUpdate) => {
    const value = await request<AdminUserSummary>(`/admin/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(update),
    });
    setAdminUsers((current) => current ? {
      ...current,
      items: current.items.map((item) => item.id === value.id ? value : item),
    } : current);
    return value;
  }, [request]);

  const loadAdminSettings = useCallback(async () => {
    if (!token) return null;
    const value = await request<AdminGlobalSettings>("/admin/settings");
    setAdminSettings(value);
    return value;
  }, [request, token]);

  const saveAdminSettings = useCallback(async (next: AdminGlobalSettings) => {
    const value = await request<AdminGlobalSettings>("/admin/settings", {
      method: "PUT",
      body: JSON.stringify(next),
    });
    setAdminSettings(value);
    return value;
  }, [request]);

  const refreshAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const nextUser = await request<User>("/auth/me");
      setUser(nextUser);

      if (nextUser.is_admin) {
        const [stats, usersPage, globalSettings] = await Promise.all([
          request<AdminStats>("/admin/stats"),
          request<AdminUsersPage>("/admin/users?page=1&page_size=25"),
          request<AdminGlobalSettings>("/admin/settings"),
        ]);
        setAdminStats(stats);
        setAdminUsers(usersPage);
        setAdminSettings(globalSettings);
        setSnapshot(null);
        setSessions([]);
        setMeals([]);
        setWearables([]);
        setMetrics({});
        return;
      }

      const [nextSnapshot, nextSessions, nextMeals, nextWearables] = await Promise.all([
        fetchLatestPlan(),
        request<WorkoutSession[]>("/workouts/sessions?limit=100"),
        request<MealLog[]>("/nutrition/meals?limit=365"),
        request<WearableConnection[]>("/wearables"),
      ]);
      const nextMetrics = await fetchMetrics(nextWearables);
      setSnapshot(nextSnapshot);
      setSessions(nextSessions);
      setMeals(nextMeals);
      setWearables(nextWearables);
      setMetrics(nextMetrics);
      setAdminStats(null);
      setAdminUsers(null);
      setAdminSettings(null);
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setLoading(false);
    }
  }, [fetchLatestPlan, fetchMetrics, request, token]);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setSnapshot(null);
      setSessions([]);
      setMeals([]);
      setWearables([]);
      setMetrics({});
      setAdminStats(null);
      setAdminUsers(null);
      setAdminSettings(null);
      setLoading(false);
      setError("");
      return;
    }
    void refreshAll();
  }, [refreshAll, token]);

  const rebuildPlan = useCallback(async (profile: PlanProfile, trigger: string) => {
    const recovery = aggregateRecoveryMetrics(wearables, metrics);
    const recentFeedback = sessions
      .filter((session) => session.status === "completed")
      .slice(0, 14)
      .map((session) => ({
        date: session.plan_date,
        type: session.workout_type,
        durationMinutes: Math.max(1, Math.round(session.elapsed_seconds / 60)),
        rpe: session.rpe,
        avgHeartRate: session.avg_heart_rate,
        caloriesBurned: session.calories_burned,
        distanceKm: session.distance_km,
      }));

    const plan = await request<PlanResponse>("/plan", {
      method: "POST",
      headers: { "Idempotency-Key": uuid() },
      body: JSON.stringify({
        profile,
        wearable: {
          connectedSources: recovery.connectedSources,
          avgSleepHours: recovery.avgSleepHours,
          avgHRV: recovery.avgHRV,
          restingHR: recovery.restingHR,
          readinessScore: recovery.readinessScore,
          recentWeeklyDistanceKm: recovery.recentWeeklyDistanceKm,
        },
        trigger,
        recentFeedback,
      }),
    });
    const next = { plan, profile };
    setSnapshot(next);
    return next;
  }, [metrics, request, sessions, wearables]);

  const estimateMeal = useCallback((text: string) => request<NutritionEstimate>(
    "/nutrition/estimate",
    { method: "POST", body: JSON.stringify({ text }) },
  ), [request]);

  const addMeal = useCallback(async (meal: Omit<MealLog, "client_meal_id" | "created_at">) => {
    const created = await request<MealLog>("/nutrition/meals", {
      method: "POST",
      body: JSON.stringify({ ...meal, client_meal_id: uuid() }),
    });
    setMeals((current) => [created, ...current]);
    return created;
  }, [request]);

  const deleteMeal = useCallback(async (id: string) => {
    await request<void>(`/nutrition/meals/${encodeURIComponent(id)}`, { method: "DELETE" });
    setMeals((current) => current.filter((meal) => meal.client_meal_id !== id));
  }, [request]);

  const clearMeals = useCallback(async () => {
    await request<void>("/nutrition/meals", { method: "DELETE" });
    setMeals([]);
  }, [request]);

  const connectWearable = useCallback(async (provider: string, code: string) => {
    await request(`/wearables/${provider}/connect`, {
      method: "POST",
      body: JSON.stringify({
        code,
        redirect_uri: window.location.origin,
      }),
    });
    await refreshWearables();
  }, [refreshWearables, request]);

  const disconnectWearable = useCallback(async (provider: string) => {
    await request(`/wearables/${provider}`, { method: "DELETE" });
    await refreshWearables();
  }, [refreshWearables, request]);

  const updateName = useCallback(async (name: string) => {
    const nextUser = await request<User>("/auth/me", {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });
    setUser(nextUser);
    return nextUser;
  }, [request]);

  const changePassword = useCallback((currentPassword: string, newPassword: string) => request<TokenResponse>(
    "/auth/change-password",
    {
      method: "POST",
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    },
  ), [request]);

  const revokeSessions = useCallback(() => request<TokenResponse>(
    "/auth/revoke-sessions",
    { method: "POST" },
  ), [request]);

  const deleteAccount = useCallback((password: string) => request<void>(
    "/auth/account",
    { method: "DELETE", body: JSON.stringify({ password }) },
  ), [request]);

  return {
    user,
    snapshot,
    sessions,
    meals,
    wearables,
    metrics,
    adminStats,
    adminUsers,
    adminSettings,
    loading,
    error,
    setError,
    refreshAll,
    refreshSessions,
    refreshMeals,
    refreshWearables,
    loadAdminStats,
    loadAdminUsers,
    updateAdminUser,
    loadAdminSettings,
    saveAdminSettings,
    rebuildPlan,
    estimateMeal,
    addMeal,
    deleteMeal,
    clearMeals,
    connectWearable,
    disconnectWearable,
    updateName,
    changePassword,
    revokeSessions,
    deleteAccount,
  };
}
